import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ImageOff, X } from 'lucide-react';
import api from '@/lib/api';

const DISMISS_KEY = 'mfm_image_missing_dismissed_until';
const DISMISS_HOURS = 24; // resurfaces daily so users actually fix it

/**
 * Yellow soft-warn banner that surfaces when the post-outage image audit
 * flagged the current user. Two cases handled:
 *   - profile_missing: their profile photo file is gone
 *   - events_missing[]: one or more of their hosted events lost its image
 *
 * Hidden for: demo accounts, unauthenticated visitors, anyone with no
 * missing images. Dismissible — auto-resurfaces every 24h until the user
 * actually re-uploads (which clears the flag server-side via PUT /profile
 * or PUT /events/{id}, so the banner naturally disappears once fixed).
 */
export default function ImageMissingBanner() {
  const { user } = useAuth();
  const [status, setStatus] = useState({ profile_missing: false, events_missing: [], any_missing: false });
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (!user || user.is_demo) return;
    api.get('/me/image-missing-status')
      .then(({ data }) => setStatus({
        profile_missing: !!data.profile_missing,
        events_missing: data.events_missing || [],
        any_missing: !!data.any_missing,
      }))
      .catch(() => {});
    try {
      const until = parseInt(sessionStorage.getItem(DISMISS_KEY) || '0', 10);
      setDismissed(until > Date.now());
    } catch {
      setDismissed(false);
    }
  }, [user]);

  if (!user || user.is_demo || !status.any_missing || dismissed) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_HOURS * 60 * 60 * 1000));
    } catch { /* private browsing */ }
    setDismissed(true);
  };

  const evCount = status.events_missing.length;

  return (
    <div
      className="bg-amber-50 border-y border-amber-200 text-amber-900 text-sm"
      role="alert"
      data-testid="image-missing-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <ImageOff className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-700" aria-hidden="true" />
          <div className="leading-snug">
            <p>
              <strong>
                {status.profile_missing && evCount > 0 && 'A few of your images need to be re-uploaded.'}
                {status.profile_missing && evCount === 0 && 'Your profile photo needs to be re-uploaded.'}
                {!status.profile_missing && evCount > 0 && (evCount === 1
                  ? 'One of your event photos needs to be re-uploaded.'
                  : `${evCount} of your event photos need to be re-uploaded.`)}
              </strong>
              {' '}
              <span className="text-amber-800">
                Some images were lost during last week's hosting outage. Sorry about that — quickest fix is to re-upload directly:
              </span>
            </p>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-amber-900">
              {status.profile_missing && (
                <Link
                  to="/profile"
                  className="underline font-medium hover:text-amber-700"
                  data-testid="image-missing-fix-profile"
                >
                  Re-upload profile photo
                </Link>
              )}
              {status.events_missing.slice(0, 3).map((ev) => (
                <Link
                  key={ev.event_id}
                  to={`/events/${ev.event_id}/edit`}
                  className="underline font-medium hover:text-amber-700"
                  data-testid={`image-missing-fix-event-${ev.event_id}`}
                  title={ev.title}
                >
                  Fix &ldquo;{(ev.title || 'event').slice(0, 30)}&rdquo;
                </Link>
              ))}
              {evCount > 3 && (
                <span className="text-amber-700 italic">+ {evCount - 3} more</span>
              )}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Hide for 24 hours"
          className="flex-shrink-0 rounded-full p-1 hover:bg-amber-100 transition-colors text-amber-700"
          data-testid="image-missing-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
